import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { exitExamApi } from '../api';
import Header from './Header';

export default function ExamResultsPortal() {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [requestCount, setRequestCount] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const submitTimeoutRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const countdownRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (requestCount >= RATE_LIMIT && countdown > 0) {
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setRequestCount(0);
            setCountdown(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [requestCount, countdown]);

  // Rate limiting: max 5 requests per minute
  const RATE_LIMIT = 5;
  const RATE_LIMIT_WINDOW = 60000; // 1 minute in milliseconds

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmittingRef.current || loading) {
      return;
    }

    // Validate username
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    // Check rate limiting
    const now = Date.now();
    if (now - lastRequestTime < 1000) { // Minimum 1 second between requests
      setError('Please wait a moment before trying again');
      return;
    }

    // Check if rate limit exceeded
    if (requestCount >= RATE_LIMIT && now - lastRequestTime < RATE_LIMIT_WINDOW) {
      const remainingTime = Math.ceil((RATE_LIMIT_WINDOW - (now - lastRequestTime)) / 1000);
      setCountdown(remainingTime);
      setError(`Request limit exceeded! You can only make ${RATE_LIMIT} requests per minute. Please wait ${remainingTime} seconds before trying again.`);
      return;
    }

    // Validate username format (5-15 alphanumeric characters and dots)
    const usernameRegex = /^[a-zA-Z0-9.]{5,15}$/;
    if (!usernameRegex.test(username.trim())) {
      setError('Username must be 5-15 characters long and contain only letters, numbers, and dots');
      return;
    }

    // Set submitting flag
    isSubmittingRef.current = true;
    setLoading(true);
    setError(null);
    setResult(null);
    setLastRequestTime(now);
    const newRequestCount = requestCount + 1;
    setRequestCount(newRequestCount);

    // If this request reaches the limit, start countdown immediately
    if (newRequestCount >= RATE_LIMIT) {
      setCountdown(60); // Start 60 second countdown immediately
    }

    // Clear any existing timeout
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }

    try {
      console.log('Submitting username:', username.trim());
      const response = await exitExamApi.getResult(username.trim());
      console.log('API response:', response);
      if (response.status === 'success') {
        // Navigate to result page with the data
        navigate('/result', { 
          state: { 
            username: username.trim(),
            resultData: response.data 
          } 
        });
      } else {
        // Check if it's a "student not found" type of error
        const errorMessage = response.message || 'Failed to fetch result';
        const isStudentNotFound = errorMessage.toLowerCase().includes('student') || 
                                 errorMessage.toLowerCase().includes('not found') || 
                                 errorMessage.toLowerCase().includes('no result');
        
        if (isStudentNotFound) {
          setError('No result record found for this username. Please check the username and try again.');
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Network or fetch error:', err);
      const errorMessage = err.message || 'Failed to process request. Please try again.';
      const isStudentNotFound = errorMessage.toLowerCase().includes('student') || 
                               errorMessage.toLowerCase().includes('not found') || 
                               errorMessage.toLowerCase().includes('no result');
      
      if (isStudentNotFound) {
        setError('No result record found for this username. Please check the username and try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
      
      // Reset rate limit after window expires
      submitTimeoutRef.current = setTimeout(() => {
        setRequestCount(0);
        setCountdown(0);
      }, RATE_LIMIT_WINDOW);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 text-base-content">
      <Header />

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link
            to="/"
            className="text-gray-600 hover:text-gray-800 flex items-center cursor-pointer transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            Back to Categories
          </Link>
        </div>

        {/* Result Access Form */}
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 space-y-6">
            {/* Form Header */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-md" style={{ background: 'rgb(18,82,166)' }}>
                <i className="fa-solid fa-user-lock text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Enter your username
                </h2>
                <p className="text-gray-600 text-sm">
                  Enter the username you used to take the exam to view your examination results
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                    <i className="fa-regular fa-id-card" style={{ color: 'rgb(18,82,166)' }}></i>
                  </span>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-gray-300 transition-all duration-200"
                    style={{ 
                      '--tw-ring-color': 'rgb(18,82,166)',
                      '--tw-border-opacity': '1',
                      'border-color': 'rgb(18,82,166)'
                    }}
                    placeholder="Enter your username (5-15 characters)"
                    required
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Username must be 5-15 characters long and contain only letters and numbers
                </p>
                <div className="mt-3 p-3 rounded-lg shadow-sm border-l-4" style={{ 
                  backgroundColor: 'rgba(18, 82, 166, 0.05)', 
                  borderLeftColor: 'rgb(18,82,166)',
                  border: '1px solid rgba(18, 82, 166, 0.1)'
                }}>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: 'rgb(18,82,166)' }}>
                      <i className="fa-solid fa-lightbulb text-white text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'rgb(18,82,166)' }}>
                        Username Format Tip
                      </p>
                      <p className="text-xs mt-1 text-gray-600">
                        If your username contains "/" (slash), please remove it. 
                        <span className="font-medium text-gray-700"> Example: "username/16" → "username"</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Request Limit Indicator - Only show when limit is reached */}
              {requestCount >= RATE_LIMIT && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-red-700 font-medium">Request Limit Exceeded</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-red-600 font-medium">
                        Wait {countdown} seconds
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                    <div 
                      className="h-2 bg-red-500 rounded-full transition-all duration-300"
                      style={{ width: `${(countdown / 60) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="w-full text-white py-3 rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'rgb(18,82,166)' }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                    Loading Results...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                    View Result
                  </>
                )}
              </button>

              {/* Error Display */}
              {error && (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    <i className="fa-solid fa-exclamation-triangle mr-2 text-red-500"></i>
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                </div>
              )}
            </form>

            {/* Help Section */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-center">
                <span 
                  className="flex items-center cursor-pointer transition-colors duration-200 text-sm hover:opacity-80" 
                  style={{ color: 'rgb(18,82,166)' }}
                  onClick={() => window.open('https://t.me/moeexitexamofficial', '_blank')}
                >
                  <i className="fa-solid fa-headset mr-2"></i>
                  Contact Support
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
